"use client";
import React from "react";
import { Calendar, Play, Circle, FileText, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { 
  parseISTDate,
  convertToIST, 
  isTestExpired, 
  isTestAvailableNow, 
  isTestVisible, 
  getTimeRemaining, 
  getTestTypeLabel 
} from '../utils/testTimeUtils';

const getTestStatus = (test) => {
  if (test.test_status === 'completed') return "completed";
  if (test.test_status === 'in_progress') return "in_progress";
  if (isTestExpired(test.scheduled_time)) return "expired";
  if (isTestAvailableNow(test.scheduled_time)) return "ready";
  return "scheduled";
};

const ScheduleItem = ({ test }) => {
  const status = getTestStatus(test);
  const timeRemaining = getTimeRemaining(test.scheduled_time);

  const handleStart = async () => {
    try {
      await fetch("/api/mark_completed", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ testId: test.test_id, testType: test.test_type })
      });
      sessionStorage.setItem("isValidAccess", "true");
      window.location.href = `/instructions?test_id=${test.test_id}`;
    } catch (error) {}
  };

  const isActive = status === "ready" || status === "in_progress";

  return (
    <div className={cn(
      "flex items-center gap-3 p-2.5 rounded-md border transition-all",
      "hover:border-primary/30 hover:bg-primary/5",
      isActive && "bg-warning/20 border-warning/40",
      status === "expired" && "opacity-50"
    )}>
      {/* Status indicator */}
      <div className={cn(
        "w-2.5 h-2.5 rounded-full shrink-0",
        status === "ready" && "bg-warning",
        status === "in_progress" && "bg-info animate-pulse",
        status === "scheduled" && "bg-muted-foreground",
        status === "expired" && "bg-error"
      )} />

      {/* Main content */}
      <div className="flex-1 min-w-0 pr-3">
        <div className="flex items-center gap-1.5 text-xs leading-normal mb-0.5">
          <span className="font-medium truncate min-w-0">{test.topic}</span>
          <span className="text-muted-foreground shrink-0">•</span>
          <span className="text-muted-foreground truncate min-w-0">{test.company_name}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground leading-normal">
          <span className="truncate min-w-0">{getTestTypeLabel(test.test_type)}</span>
          <span className="shrink-0">•</span>
          <span className="flex items-center gap-1 shrink-0">
            <Calendar className="w-3 h-3" />
            <span className="whitespace-nowrap">{convertToIST(test.scheduled_time)}</span>
          </span>
        </div>
      </div>

      {/* Action */}
      <div className="shrink-0 ml-auto">
        {(status === "ready" || status === "in_progress") && (
          <Button 
            size="sm" 
            onClick={handleStart} 
            className="h-7 text-xs px-2.5 gap-1.5 leading-normal whitespace-nowrap"
          >
            <Play className="w-3 h-3 shrink-0" />
            {status === "in_progress" ? "Continue" : "Start"}
          </Button>
        )}
        
        {status === "scheduled" && timeRemaining && (
          <span className="text-xs text-muted-foreground font-medium whitespace-nowrap leading-normal">in {timeRemaining}</span>
        )}
        
        {status === "expired" && (
          <span className="text-xs text-error font-medium leading-normal whitespace-nowrap">Missed</span>
        )}
      </div>
    </div>
  );
};

const UpcomingSchedule = ({ tests }) => {
  const safeTests = Array.isArray(tests) ? tests : [];
  const pendingTests = safeTests.filter(test => test.test_status !== 'completed' && isTestVisible(test.scheduled_time));

  // Sort: active/ready tests first, then by scheduled time
  const sortedTests = [...pendingTests].sort((a, b) => {
    const statusA = getTestStatus(a);
    const statusB = getTestStatus(b);
    
    // Priority: in_progress > ready > scheduled > expired
    const priority = { in_progress: 0, ready: 1, scheduled: 2, expired: 3 };
    const priorityDiff = priority[statusA] - priority[statusB];
    
    if (priorityDiff !== 0) return priorityDiff;
    
    // Same priority, sort by scheduled time (earlier first)
    return parseISTDate(a.scheduled_time) - parseISTDate(b.scheduled_time);
  });

  return (
    <Card className="border shadow-sm overflow-hidden bg-background">
      <CardHeader className="p-3 border-b border-border bg-background">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-primary shrink-0" />
          <CardTitle className="text-sm font-semibold leading-normal">Upcoming</CardTitle>
          {sortedTests.length > 0 && (
            <Badge variant="secondary" className="ml-auto text-xs leading-normal">
              {sortedTests.length}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-3">
        {sortedTests.length > 0 ? (
          <div className="space-y-2">
            {sortedTests.map((test) => (
              <ScheduleItem key={test.test_id} test={test} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center mb-2">
              <CalendarDays className="w-4 h-4 text-muted-foreground" />
            </div>
            <p className="text-xs font-medium text-foreground leading-normal">No Tests Scheduled</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingSchedule;
